import { type ComponentProps } from "react";

import {
  parseGid,
  type Cart,
  type CartLineQuantityAdjustButton,
} from "@shopify/hydrogen-react";
import { type CartLine } from "@shopify/hydrogen-react/storefront-api-types";

import { useSectionSettings } from "@react/sections/Root/SectionSettingsContext";
// @ts-expect-error - no types
import AnalyticsProvider from "@root/scripts/lib/analyticsProvider.js";
import {
  type TablescapeState,
  type CartUpsellProductMetadata,
  type CartSectionSettings,
  type CartProduct,
  type CartProductVariant,
  type UseCartLine,
} from "@tablescape/state";

export enum CartAnalyticsEvents {
  AMPLITUDE_PRODUCT_ADDED = "productAdded",
  CART_PRODUCT_VARIANT_DRAWER_VIEWED = "viewCartProductVariantDrawer",
  CART_VIEWED = "cartViewed",
  CLICK_QUANTITY_SELECTOR = "clickQuantitySelector",
  CONTINUE_TO_CHECKOUT = "continueToCheckout",
  GTM_PRODUCT_ADDED = "gtmProductAdded",
  PRODUCT_ADDED = "addedToCart",
  PRODUCT_BUNDLE_ADDED = "bundleAddedToCart",
  UPSELL_PRODUCTS_VIEWED = "viewCartUpsells",
  UPSELL_PRODUCT_ADDED = "clickAddFromSlideout",
}

const getAnalyticsId = (id: string) => parseFloat(parseGid(id).id);
const getAnalyticsPrice = (price: string) => parseFloat(price).toFixed(2);
const getVariantName = (line: CartLine) =>
  line.merchandise.title !== "Default Title"
    ? `${line.merchandise.product.title} - ${line.merchandise.title}`
    : line.merchandise.product.title;

type CartUpsellAnalyticsEventData = {
  customer: CartSectionSettings["meta"]["customer"];
  eventName: Extract<
    CartAnalyticsEvents,
    | CartAnalyticsEvents.UPSELL_PRODUCT_ADDED
    | CartAnalyticsEvents.UPSELL_PRODUCTS_VIEWED
  >;
  lines: CartLine[];
  market: CartSectionSettings["meta"]["localization"]["country"];
} & Pick<Cart, "id">;

type CartProgressionAnalyticsEventData = {
  customer: CartSectionSettings["meta"]["customer"];
  eventName: Extract<
    CartAnalyticsEvents,
    CartAnalyticsEvents.CART_VIEWED | CartAnalyticsEvents.CONTINUE_TO_CHECKOUT
  >;
  lines: CartLine[];
  market: CartSectionSettings["meta"]["localization"]["country"];
} & Pick<Cart, "checkoutUrl" | "cost" | "id">;

type CartAdditionAnalyticsEventData = {
  eventName: Extract<
    CartAnalyticsEvents,
    | CartAnalyticsEvents.AMPLITUDE_PRODUCT_ADDED
    | CartAnalyticsEvents.GTM_PRODUCT_ADDED
    | CartAnalyticsEvents.CLICK_QUANTITY_SELECTOR
  >;
  line: CartLine;
  market: CartSectionSettings["meta"]["localization"]["country"];
};

const getValidCartLines = (lines: Cart["lines"], validateMerchandise = false) =>
  (validateMerchandise
    ? lines?.filter(
        (
          line
        ): line is CartLine & {
          merchandise: NonNullable<CartLine["merchandise"]>;
        } => !!line?.id && !!line?.merchandise
      )
    : lines?.filter((line): line is CartLine => !!line?.id)) ?? [];

const getEventDetails = (
  eventData:
    | CartUpsellAnalyticsEventData
    | CartProgressionAnalyticsEventData
    | CartAdditionAnalyticsEventData
) => {
  const { eventName, market } = eventData;

  switch (eventName) {
    case CartAnalyticsEvents.AMPLITUDE_PRODUCT_ADDED: {
      const { line } = eventData;

      return {
        eventName,
        eventData: {
          "product category": line.merchandise.product.productType,
          "product id": getAnalyticsId(line.merchandise.product.id),
          "product name": line.merchandise.product.title,
          "product price": getAnalyticsPrice(line.merchandise.price.amount),
          "variant id": getAnalyticsId(line.merchandise.id),
          "variant name": getVariantName(line),
          "variant price": getAnalyticsPrice(line.merchandise.price.amount),
          "variant sku": line.merchandise.sku,
          "from url": window.location.href,
          "from path": `${window.location.pathname}${
            window.location.search ?? ""
          }${window.location.hash ?? ""}`,
          market,
        },
      };
    }

    case CartAnalyticsEvents.CART_VIEWED:
    case CartAnalyticsEvents.CONTINUE_TO_CHECKOUT: {
      const { checkoutUrl, cost, customer, id, lines } = eventData;

      return {
        eventName,
        eventData: {
          cart: {
            "logged in": customer.isLoggedIn,
            "shopify customer id": customer.id,
            vendor: "Our Place",
            "cart id": parseGid(id).id,
            "cart value": parseFloat(cost?.totalAmount?.amount ?? "0"),
            "product name": lines.map((line) => line.merchandise.product.title),
            "variant name": lines.map(getVariantName),
            msrp: lines.map((line) =>
              parseFloat(line.merchandise.price.amount)
            ),
            "product price": lines.map((line) =>
              parseFloat(line.cost.totalAmount.amount)
            ),
            "total discount": lines.map(
              (line) =>
                parseFloat(line.merchandise.price.amount) * line.quantity -
                parseFloat(line.cost.totalAmount.amount)
            ),
            product_id: lines.map((line) =>
              getAnalyticsId(line.merchandise.product.id)
            ),
            sku: lines.map((line) => line.merchandise.sku),
            quantity: lines.map((line) => line.quantity),
            market,
          },
          checkoutUrl,
        },
      };
    }

    case CartAnalyticsEvents.GTM_PRODUCT_ADDED: {
      const { line } = eventData;
      return {
        eventName,
        eventData: {
          product: {
            category: line.merchandise.product.productType,
            id: getAnalyticsId(line.merchandise.product.id),
            name: line.merchandise.product.title,
          },
          variant: {
            id: getAnalyticsId(line.merchandise.id),
            name: line.merchandise.title,
            price: getAnalyticsPrice(line.merchandise.price.amount),
          },
          quantity: line.quantity,
          currency: line.cost.totalAmount.currencyCode,
          market,
        },
      };
    }

    case CartAnalyticsEvents.CLICK_QUANTITY_SELECTOR: {
      const { line } = eventData;

      return {
        eventName,
        eventData: {
          "page section": "cart",
          "product name": line.merchandise.product.title,
          "product color": getVariantName(line),
          market,
        },
      };
    }

    case CartAnalyticsEvents.UPSELL_PRODUCT_ADDED:
    case CartAnalyticsEvents.UPSELL_PRODUCTS_VIEWED: {
      const { customer, id, lines } = eventData;

      return {
        eventName,
        eventData: {
          "cart token": parseGid(id).id,
          "items in cart - product titles": lines.map(
            (line) => line.merchandise.product.title
          ),
          "items in cart - variant titles": lines.map(getVariantName),
          "items in cart - variant ids": lines.map((line) =>
            getAnalyticsId(line.merchandise.id)
          ),
          "items in cart - prices": lines.map((line) =>
            getAnalyticsPrice(line.cost.totalAmount.amount)
          ),
          "items in cart - skus": lines.map((line) => line.merchandise.sku),
          "customer - logged in": customer.isLoggedIn,
          "customer - shopify id": customer.id,
          "customer - email": customer.email,
          "customer - total orders": customer.ordersCount,
          "customer - total spent": customer.totalSpent,
          market,
        },
      };
    }
  }

  return {
    eventName,
    eventData: {},
  };
};

const getReporters = (
  market: CartSectionSettings["meta"]["localization"]["country"]
) => {
  const analyticsProvider = new AnalyticsProvider();

  return {
    analytics: analyticsProvider,

    reportAddToCartEvent({
      bundleProduct,
      cost,
      lines,
    }: TablescapeState["cart"]) {
      const isBundle = !!bundleProduct;
      const validLines = getValidCartLines(lines);

      // Klaviyo-specific event data
      const klaviyoCart = {
        currency: cost?.totalAmount?.currencyCode,
        items: validLines.map((line) => {
          return {
            id: String(line.id),
            image: line.merchandise.image?.url,
            market,
            price: parseFloat(line.merchandise.price.amount ?? "0") * 100,
            title: getVariantName(line),
            url: line.merchandise.product.onlineStoreUrl,
            variant_id: getAnalyticsId(line.merchandise.id),
            vendor: line.merchandise.product.vendor,
          };
        }),
        totalAmount: parseFloat(cost?.totalAmount?.amount ?? "0"),
      };

      if (!isBundle) {
        validLines.forEach((line) => {
          window.maestro.fireTrackEvent(CartAnalyticsEvents.PRODUCT_ADDED, {
            cart: klaviyoCart,
            item: {
              category: line.merchandise.product.productType,
              id: String(line.id),
              image: line.merchandise.image?.url,
              productId: getAnalyticsId(line.merchandise.product.id),
              price: parseFloat(line.merchandise.price.amount),
              quantity: line.quantity,
              title: getVariantName(line),
              type: "product_group",
              url: line.merchandise.product.onlineStoreUrl,
            },
          });
        });

        // Amplitude-specific event
        validLines.forEach((line) => {
          const { eventData, eventName } = getEventDetails({
            eventName: CartAnalyticsEvents.AMPLITUDE_PRODUCT_ADDED,
            line,
            market,
          });

          window.maestro.fireTrackEvent(eventName, eventData);
        });

        // Google Tag Manager event
        window.dataLayer = window.dataLayer || [];
        validLines.forEach((line) => {
          const { eventData, eventName } = getEventDetails({
            eventName: CartAnalyticsEvents.GTM_PRODUCT_ADDED,
            line,
            market,
          });

          window.dataLayer.push({ event: eventName, ...eventData });
        });

        validLines.forEach((line) => {
          const price = line?.merchandise?.price?.amount
            ? parseFloat(line.merchandise.price.amount) / 100
            : 1;

          analyticsProvider.googleAds(price);
        });
      } else if (bundleProduct) {
        validLines.forEach((line) => {
          const { eventData, eventName } = getEventDetails({
            eventName: CartAnalyticsEvents.AMPLITUDE_PRODUCT_ADDED,
            line,
            market,
          });

          window.maestro.fireTrackEvent(eventName, {
            ...eventData,
            "bundle name": bundleProduct.title,
          });
        });

        window.maestro.fireTrackEvent(
          CartAnalyticsEvents.PRODUCT_BUNDLE_ADDED,
          {
            cart: klaviyoCart,
            cartItems: validLines.map((line) => ({
              category: line.merchandise.product.productType,
              image: line.merchandise.image?.url,
              price: parseFloat(line.merchandise.price.amount),
              productId: getAnalyticsId(line.merchandise.product.id),
              quantity: line.quantity,
              title: getVariantName(line),
              type: "product_group",
              url: line.merchandise.product.onlineStoreUrl,
            })),
            bundleProduct: {
              ...bundleProduct,
              market,
            },
          }
        );

        analyticsProvider.track({
          fbq: [
            "trackCustom",
            "AddBundle",
            {
              bundleName: bundleProduct.title,
              bundlePrice: bundleProduct.price,
            },
          ],
          ga: [
            "send",
            {
              hitType: "event",
              eventCategory: "Bundles",
              eventAction: "Added Bundle on PDP",
              eventLabel: bundleProduct.title,
              eventValue: bundleProduct.price,
            },
          ],
        });
      }
    },

    reportCartProductVariantDrawerViewedEvent({
      isProductUpdate,
      product,
      selectedVariant,
    }: {
      isProductUpdate: boolean;
      product: CartProduct;
      selectedVariant: CartProductVariant;
    }) {
      window.maestro.fireTrackEvent(
        CartAnalyticsEvents.CART_PRODUCT_VARIANT_DRAWER_VIEWED,
        {
          market,
          "product name": product.title,
          "product price": getAnalyticsPrice(selectedVariant.price.amount),
          source: isProductUpdate ? "edit" : "upsells",
        }
      );
    },

    reportCartViewedEvent({
      checkoutUrl,
      cost,
      customer,
      id,
      lines,
    }: Cart & { customer: CartSectionSettings["meta"]["customer"] }) {
      const validLines = getValidCartLines(lines, true);

      const { eventData, eventName } = getEventDetails({
        eventName: CartAnalyticsEvents.CART_VIEWED,
        checkoutUrl,
        cost,
        customer,
        id,
        lines: validLines,
        market,
      });

      window.maestro.fireTrackEvent(eventName, eventData);
    },

    reportContinueToCheckoutEvent({
      checkoutUrl,
      cost,
      customer,
      id,
      lines,
    }: Cart & { customer: CartSectionSettings["meta"]["customer"] }) {
      const validLines = getValidCartLines(lines, true);

      const { eventData, eventName } = getEventDetails({
        eventName: CartAnalyticsEvents.CONTINUE_TO_CHECKOUT,
        checkoutUrl,
        cost,
        customer,
        id,
        lines: validLines,
        market,
      });

      window.maestro.fireTrackEvent(eventName, eventData);
    },

    reportQuantitySelectorEvent({
      adjust,
      line,
      quantity,
    }: {
      adjust: ComponentProps<typeof CartLineQuantityAdjustButton>["adjust"];
      line: UseCartLine;
      quantity: number;
    }) {
      const validLines = getValidCartLines([line], true);

      validLines.forEach((line) => {
        const { eventData, eventName } = getEventDetails({
          eventName: CartAnalyticsEvents.CLICK_QUANTITY_SELECTOR,
          line,
          market,
        });

        const target =
          adjust === "decrease"
            ? "minus"
            : adjust === "increase"
            ? "plus"
            : "remove";

        window.maestro.fireTrackEvent(eventName, {
          ...eventData,

          quantity,
          target,
        });
      });
    },

    reportUpsellAddToCartEvent({
      customer,
      id,
      lines,
      position,
      upsell,
    }: Cart & {
      customer: CartSectionSettings["meta"]["customer"];
      position: number;
      upsell: CartUpsellProductMetadata;
    }) {
      const validLines = getValidCartLines(lines, true);

      const { eventData, eventName } = getEventDetails({
        eventName: CartAnalyticsEvents.UPSELL_PRODUCT_ADDED,
        customer,
        id,
        lines: validLines,
        market,
      });

      window.maestro.fireTrackEvent(eventName, {
        ...eventData,

        "upsells added - product titles": [upsell.product.title],
        "upsells added - variant titles": [
          `${upsell.product.title} - ${upsell.selectedVariant.title}`,
        ],
        "upsells added - variant ids": [
          getAnalyticsId(upsell.selectedVariant.id),
        ],
        "upsells added - prices": [upsell.selectedVariant.price.amount].map(
          getAnalyticsPrice
        ),
        "upsells added - skus": [upsell.selectedVariant.sku],
        "upsells added - positions": [position],
      });
    },

    reportUpsellsViewedEvent(
      {
        customer,
        dataDrivenUpsells,
        id,
        lines,
      }: Cart & {
        customer: CartSectionSettings["meta"]["customer"];
        dataDrivenUpsells: boolean;
      },
      upsells: CartUpsellProductMetadata[]
    ) {
      const validLines = getValidCartLines(lines, true);

      const { eventData, eventName } = getEventDetails({
        eventName: CartAnalyticsEvents.UPSELL_PRODUCTS_VIEWED,
        customer,
        id,
        lines: validLines,
        market,
      });

      window.maestro.fireTrackEvent(eventName, {
        ...eventData,

        "upsells shown - product titles": upsells.map(
          (upsell) => upsell.product.title
        ),
        "upsells shown - variant titles": upsells.map(
          (upsell) =>
            `${upsell.product.title} - ${upsell.selectedVariant.title}`
        ),
        "upsells shown - variant ids": upsells.map((upsell) =>
          getAnalyticsId(upsell.selectedVariant.id)
        ),
        "upsells shown - prices": upsells.map((upsell) =>
          getAnalyticsPrice(upsell.selectedVariant.price.amount)
        ),
        "upsells shown - skus": upsells.map(
          (upsell) => upsell.selectedVariant.sku
        ),

        "data driven upsells": dataDrivenUpsells,
      });
    },
  };
};

export function useCartAnalytics() {
  const {
    meta: {
      localization: { country },
    },
  } = useSectionSettings<CartSectionSettings>();

  return getReporters(country);
}
